/**
 * @fileoverview Progress indicator showing current step in onboarding flow.
 * @module features/onboarding/components/StepIndicator
 */

import { ReactElement } from 'react'
import { cn } from '@/lib/utils'

interface StepIndicatorProps {
  /** Current step index (0-based) */
  currentStep: number
  /** Total number of steps */
  totalSteps: number
  /** Array of completed step indices */
  completedSteps: number[]
  /** Callback when a step is clicked */
  onStepClick?: ((stepIndex: number) => void) | undefined
  /** Additional CSS classes */
  className?: string
}

/**
 * Horizontal dot progress indicator for onboarding wizard.
 * Shows current, completed, and upcoming steps visually.
 * Completed steps are clickable for navigation.
 *
 * @component
 * @example
 * ```tsx
 * <StepIndicator
 *   currentStep={2}
 *   totalSteps={8}
 *   completedSteps={[0, 1]}
 *   onStepClick={(index) => goToStep(index)}
 * />
 * ```
 */
export function StepIndicator({
  currentStep,
  totalSteps,
  completedSteps,
  onStepClick,
  className,
}: StepIndicatorProps): ReactElement {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {Array.from({ length: totalSteps }, (_, index) => {
        const isCompleted = completedSteps.includes(index)
        const isCurrent = index === currentStep
        const isUpcoming = index > currentStep && !isCompleted
        const isClickable = isCompleted && !isCurrent && onStepClick

        return (
          <button
            key={index}
            type="button"
            onClick={() => isClickable && onStepClick(index)}
            disabled={!isClickable}
            className={cn(
              'h-2 w-2 rounded-full transition-all duration-300',
              isCurrent && 'bg-primary scale-125',
              isCompleted && !isCurrent && 'bg-primary/60',
              isUpcoming && 'bg-muted-foreground/30',
              isClickable && 'cursor-pointer hover:scale-150 hover:bg-primary/80',
              !isClickable && 'cursor-default'
            )}
            aria-label={`Step ${index + 1} of ${totalSteps}${isCurrent ? ' (current)' : ''}${isCompleted ? ' (completed)' : ''}`}
          />
        )
      })}
    </div>
  )
}
